import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useCurrency } from "@/lib/currency";
import RecentActivityTicker from "@/components/RecentActivityTicker";

function pct(a, b) { return Math.min(100, Math.round((a / b) * 100)); }

export default function Marketplace() {
  const { t } = useTranslation();
  const { fmt: fmtMoney } = useCurrency();
  const [icos, setIcos] = useState([]);
  const [filter, setFilter] = useState("active");
  const [stats, setStats] = useState({ yield_index: 0, active_offerings: 0, total_raised: 0, total_distributed: 0 });

  useEffect(() => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/icos${q}`).then(({ data }) => setIcos(data)).catch(() => {});
  }, [filter]);

  useEffect(() => { api.get("/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  return (
    <>
      <RecentActivityTicker />
      <div className="max-w-[1400px] mx-auto px-6 py-12">
      {/* LIVE YIELD INDEX BANNER */}
      <div data-testid="yield-index-banner" className="bg-black text-white p-6 md:p-8 border border-black mb-10 grid grid-cols-12 gap-6 items-center">
        <div className="col-span-12 md:col-span-6">
          <div className="font-mono-label text-[11px] text-[#FFCC00] flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FFCC00] animate-pulse"/>
            {t("mp_yield_index_title")}
          </div>
          <div className="flex items-baseline gap-3 mt-3">
            <div data-testid="yield-index-value" className="font-display text-6xl md:text-7xl font-black tracking-tight text-[#FFCC00]">
              {stats.yield_index ? `${stats.yield_index.toFixed(2)}%` : "—"}
            </div>
            <div className="font-mono-label text-[10px] text-zinc-400">APY</div>
          </div>
          <p className="text-zinc-400 text-xs mt-3 max-w-md">{t("mp_yield_index_sub")}</p>
        </div>

        <div className="col-span-12 md:col-span-6 grid grid-cols-3 gap-px bg-white/10">
          <div className="bg-black p-4">
            <div className="font-mono-label text-[9px] text-zinc-400">{t("stat_active_offerings")}</div>
            <div className="font-display text-2xl font-black mt-1">{stats.active_offerings || 0}</div>
          </div>
          <div className="bg-black p-4">
            <div className="font-mono-label text-[9px] text-zinc-400">{t("stat_raised")}</div>
            <div className="font-display text-2xl font-black mt-1">{fmtMoney(stats.total_raised)}</div>
          </div>
          <div className="bg-black p-4">
            <div className="font-mono-label text-[9px] text-zinc-400">{t("stat_distributed")}</div>
            <div className="font-display text-2xl font-black mt-1">{fmtMoney(stats.total_distributed)}</div>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-end mb-10 flex-wrap gap-4">
        <div>
          <div className="font-mono-label text-[11px] text-[#002FA7]">⎯⎯ {t("mp_tag")}</div>
          <h1 className="font-display text-5xl font-black mt-2">{t("mp_title")}</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            ["active", t("mp_filter_active")],
            ["funded", t("mp_filter_funded")],
            ["expired", t("mp_filter_expired")],
            ["all", t("mp_filter_all")],
          ].map(([k, l]) => (
            <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)}
              className={`font-mono-label text-[10px] px-4 py-2 border ${
                filter === k ? "bg-black text-white border-black" : "border-black/30 hover:border-black"
              }`}>{l}</button>
          ))}
        </div>
      </div>

      {icos.length === 0 ? (
        <div className="border border-black/10 p-12 text-center text-zinc-500 font-mono-label text-xs">
          {t("mp_empty")}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {icos.map((ico) => (
            <Link to={`/ico/${ico.id}`} key={ico.id} data-testid={`ico-card-${ico.id}`}
              className="group border border-black/10 hover:border-black hover:-translate-y-0.5 transition bg-white overflow-hidden">
              <div className="aspect-[4/3] overflow-hidden bg-zinc-100">
                <img src={ico.property_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
              </div>
              <div className="p-5 space-y-4">
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <div className="font-mono-label text-[10px] text-zinc-500">{ico.duration_months} {t("months")} · {ico.amc_name || "—"}</div>
                    <div className="font-display text-xl font-bold mt-1">{ico.property_name}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className={`rounded-none font-mono-label text-[10px] ${
                      ico.status === "active" ? "bg-[#002FA7]" : ico.status === "funded" ? "bg-black" : "bg-zinc-400"
                    }`}>{ico.status}</Badge>
                    {ico.expected_annual_return_pct != null && (
                      <span className="bg-[#FFCC00] text-black px-2 py-0.5 font-mono-label text-[10px]">
                        {ico.expected_annual_return_pct.toFixed(1)}% APY
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-px bg-black/10">
                  <div className="bg-white p-3">
                    <div className="font-mono-label text-[9px] text-zinc-500">{t("mp_rent_per_mo")}</div>
                    <div className="font-display text-lg font-bold">{fmtMoney(ico.monthly_rent)}</div>
                  </div>
                  <div className="bg-white p-3">
                    <div className="font-mono-label text-[9px] text-zinc-500">{t("mp_coin_price")}</div>
                    <div className="font-display text-lg font-bold">{fmtMoney(ico.price_per_coin)}</div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between font-mono-label text-[10px] mb-1">
                    <span>{ico.coins_sold.toLocaleString()} / {ico.total_coins.toLocaleString()}</span>
                    <span className="text-[#002FA7]">{pct(ico.coins_sold, ico.total_coins)}%</span>
                  </div>
                  <div className="h-1 bg-black/10">
                    <div className="h-full bg-[#002FA7]" style={{ width: `${pct(ico.coins_sold, ico.total_coins)}%` }}/>
                  </div>
                </div>

                {ico.investor_count > 0 && (
                  <div
                    data-testid={`investor-badge-${ico.id}`}
                    className="flex items-center gap-2 text-[10px] font-mono-label text-zinc-600 border border-black/10 bg-zinc-50 px-2.5 py-1.5"
                  >
                    <span className="inline-flex -space-x-1">
                      {[...Array(Math.min(3, ico.investor_count))].map((_, idx) => (
                        <span
                          key={`av-${idx}`}
                          className="inline-block w-4 h-4 rounded-full border border-white bg-[#002FA7]"
                          style={{ backgroundColor: ["#002FA7", "#FFCC00", "#FF3B30"][idx] || "#002FA7" }}
                        />
                      ))}
                    </span>
                    <span className="tabular-nums">
                      {t("mp_investors", { count: ico.investor_count })}
                    </span>
                  </div>
                )}

                <Button className="w-full rounded-none bg-black hover:bg-zinc-800 h-10 font-mono-label text-[11px]">
                  {t("mp_view_offering")}
                </Button>
              </div>
            </Link>
          ))}
        </div>
      )}
      </div>
    </>
  );
}
